import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

function hashColor(seed) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h << 5) - h + seed.charCodeAt(i);
  const hue = Math.abs(h) % 360;
  return `hsl(${hue}, 55%, 38%)`;
}

function initials(name) {
  if (!name) return "?";
  const parts = name.split(" ").filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts[parts.length - 1]?.[0] || "")).toUpperCase();
}

// Icon-style portrait. Tries the real image, falls back to SVG initials on error / when null.
function PlayerPortrait({ player }) {
  const url = player?.image_url || null;
  const color = hashColor(player?.nationality || "x");
  const [errored, setErrored] = useState(false);
  const showImage = url && !errored;

  return (
    <div
      data-testid="card-player-portrait"
      className="w-20 h-20 rounded-full overflow-hidden flex items-center justify-center text-3xl font-bold shadow-inner"
      style={{ background: "#0a0a0a", border: `2px solid ${color}` }}
    >
      {showImage ? (
        <img
          src={url}
          alt={player?.name || ""}
          onError={() => setErrored(true)}
          referrerPolicy="no-referrer"
          className="w-full h-full object-cover"
          data-testid="card-player-img"
        />
      ) : (
        <span data-testid="card-player-initials">{initials(player?.name)}</span>
      )}
    </div>
  );
}

function TeamBadge({ team }) {
  const url = team?.logo_url || null;
  const color = team?.badge_color || "#888888";
  const [errored, setErrored] = useState(false);
  const showImage = url && !errored;

  return (
    <div
      data-testid="card-team-badge"
      className="w-9 h-9 rounded-full overflow-hidden flex items-center justify-center text-[11px] font-bold"
      style={{ background: showImage ? "#ffffff" : color, color: "#0a0a0a" }}
    >
      {showImage ? (
        <img
          src={url}
          alt={team?.name || ""}
          onError={() => setErrored(true)}
          referrerPolicy="no-referrer"
          className="w-full h-full object-contain"
          data-testid="card-team-img"
        />
      ) : (
        <span data-testid="card-team-initials">{initials(team?.name)}</span>
      )}
    </div>
  );
}

function PlayerCard({ player, team, startYear, endYear }) {
  const playerColor = hashColor(player?.nationality || "x");
  return (
    <div
      data-testid="puzzle-card"
      className="rounded-2xl overflow-hidden border border-white/15 shadow-lg bg-zinc-950 text-zinc-100 flex flex-col"
      style={{ minWidth: 220 }}
    >
      <div
        className="px-4 py-5 flex flex-col items-center gap-2"
        style={{ background: `linear-gradient(160deg, ${playerColor}, #0a0a0a)` }}
      >
        <div className="text-xs uppercase tracking-widest opacity-70">{player?.position || "—"}</div>
        <PlayerPortrait player={player} />
        <div className="text-base font-semibold text-center mt-1" data-testid="card-player-name">
          {player?.name || "Unknown"}
        </div>
        <div className="text-xs opacity-80">{player?.nationality}</div>
      </div>
      <div className="px-4 py-3 flex items-center gap-3 bg-zinc-900 border-t border-white/10">
        <TeamBadge team={team} />
        <div className="flex-1">
          <div className="text-sm font-medium" data-testid="card-team-name">{team?.name || "—"}</div>
          <div className="text-[11px] opacity-70">
            {startYear}{endYear !== startYear ? `–${endYear}` : ""}
            {team?.type === "national_team" ? " · National team" : ""}
          </div>
        </div>
      </div>
    </div>
  );
}

function AnswerReveal({ correct }) {
  if (!correct) return null;
  return (
    <div
      data-testid="puzzle-answer"
      className="rounded-2xl border-2 border-emerald-500/60 bg-zinc-950 text-zinc-100 p-5 flex items-center gap-4"
    >
      <PlayerPortrait player={correct} />
      <div>
        <div className="text-xs uppercase tracking-widest text-emerald-400">Answer</div>
        <div className="text-xl font-semibold" data-testid="answer-name">{correct.name}</div>
        <div className="text-xs opacity-70">{correct.nationality} · fame {correct.fame_score}</div>
      </div>
    </div>
  );
}

export default function Debug() {
  const [stats, setStats] = useState(null);
  const [statsErr, setStatsErr] = useState(null);
  const [puzzle, setPuzzle] = useState(null);
  const [puzzleErr, setPuzzleErr] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [loadingDiff, setLoadingDiff] = useState(null);
  const [validation, setValidation] = useState(null);

  const refreshStats = useCallback(async () => {
    try {
      const { data } = await api.get(`/debug/seed-status`);
      setStats(data);
      setStatsErr(null);
    } catch (e) {
      setStatsErr(e.message);
    }
  }, []);

  useEffect(() => {
    refreshStats();
    const id = setInterval(refreshStats, 5000);
    return () => clearInterval(id);
  }, [refreshStats]);

  const fetchPuzzle = async (difficulty) => {
    setLoadingDiff(difficulty);
    setRevealed(false);
    setValidation(null);
    setPuzzleErr(null);
    try {
      const { data } = await api.get(`/debug/puzzle`, { params: { difficulty } });
      setPuzzle(data);
    } catch (e) {
      setPuzzleErr(e.response?.data?.detail || e.message);
      setPuzzle(null);
    } finally {
      setLoadingDiff(null);
    }
  };

  const runValidation = async () => {
    if (!puzzle) return;
    try {
      const { data } = await api.get(`/debug/validate-puzzle/${puzzle.id}`);
      setValidation(data);
    } catch (e) {
      setValidation({ valid: false, error: e.message });
    }
  };

  const pb = stats?.puzzles_by_difficulty || {};
  const t = stats?.targets || {};
  const ready = stats?.ready;
  const ic = stats?.image_coverage || {};

  return (
    <div data-testid="debug-page" className="min-h-screen bg-zinc-950 text-zinc-100 px-6 py-10">
      <div className="max-w-5xl mx-auto space-y-8">
        <header className="flex items-baseline justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-emerald-400">footguesser · phase 0</div>
            <h1 className="text-4xl sm:text-5xl font-bold mt-2">Football data console</h1>
            <p className="text-sm opacity-70 mt-2 max-w-xl">
              Hand-curated player &amp; team graph + classic-mode puzzle engine. Refreshing every 5s.
            </p>
          </div>
          <button
            data-testid="refresh-stats-btn"
            onClick={refreshStats}
            className="px-4 py-2 rounded-full bg-zinc-800 hover:bg-zinc-700 text-sm font-medium transition-colors"
          >
            Refresh
          </button>
        </header>

        <section data-testid="stats-grid" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {statsErr && (
            <div data-testid="stats-error" className="col-span-full text-red-400 text-sm">
              {statsErr}
            </div>
          )}
          {[
            ["Players", stats?.players, 180],
            ["Teams", stats?.teams, 75],
            ["Stints", stats?.stints, 400],
            ["Edges", stats?.edges, 1000],
          ].map(([label, value, target]) => (
            <div key={label} className="rounded-xl border border-white/10 bg-zinc-900 p-4">
              <div className="text-[11px] uppercase tracking-wider opacity-60">{label}</div>
              <div className="text-2xl font-semibold mt-1" data-testid={`stat-${label.toLowerCase()}`}>
                {value ?? "…"}
              </div>
              <div className="text-[11px] opacity-50 mt-1">target ≥ {target}</div>
            </div>
          ))}
          {["medium", "hard", "insane"].map((d) => (
            <div key={d} className="rounded-xl border border-white/10 bg-zinc-900 p-4">
              <div className="text-[11px] uppercase tracking-wider opacity-60">{d} puzzles</div>
              <div className="text-2xl font-semibold mt-1" data-testid={`stat-puzzles-${d}`}>
                {pb[d] ?? "…"}
              </div>
              <div className="text-[11px] opacity-50 mt-1">target {t[d] ?? "—"}</div>
            </div>
          ))}
          <div className="rounded-xl border border-white/10 bg-zinc-900 p-4">
            <div className="text-[11px] uppercase tracking-wider opacity-60">Player images</div>
            <div className="text-2xl font-semibold mt-1" data-testid="stat-players-with-image">{ic.players_with_image ?? "…"}</div>
            <div className="text-[11px] opacity-50 mt-1">have wiki photo</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-zinc-900 p-4">
            <div className="text-[11px] uppercase tracking-wider opacity-60">Team logos</div>
            <div className="text-2xl font-semibold mt-1" data-testid="stat-teams-with-image">{ic.teams_with_image ?? "…"}</div>
            <div className="text-[11px] opacity-50 mt-1">have wiki crest</div>
          </div>
        </section>

        {ready === false && (
          <div data-testid="bootstrap-banner" className="rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-200 text-sm px-4 py-3">
            Bootstrap still in progress — puzzle pool is filling. You can still fetch a puzzle once any difficulty has ≥1.
          </div>
        )}

        <section className="space-y-4">
          <div className="flex flex-wrap gap-3">
            {["medium", "hard", "insane"].map((d) => (
              <button
                key={d}
                data-testid={`btn-puzzle-${d}`}
                disabled={loadingDiff === d}
                onClick={() => fetchPuzzle(d)}
                className="px-5 py-2.5 rounded-full bg-emerald-500 text-zinc-950 font-semibold hover:bg-emerald-400 disabled:opacity-50 transition-colors"
              >
                {loadingDiff === d ? "loading…" : `Get ${d} puzzle`}
              </button>
            ))}
            {puzzle && (
              <button
                data-testid="btn-reveal"
                onClick={() => setRevealed(true)}
                disabled={revealed}
                className="px-5 py-2.5 rounded-full bg-zinc-800 hover:bg-zinc-700 text-sm font-medium disabled:opacity-50 transition-colors"
              >
                {revealed ? "Revealed" : "Reveal answer"}
              </button>
            )}
            {puzzle && (
              <button
                data-testid="btn-validate"
                onClick={runValidation}
                className="px-5 py-2.5 rounded-full bg-zinc-800 hover:bg-zinc-700 text-sm font-medium transition-colors"
              >
                Run validation
              </button>
            )}
          </div>

          {puzzleErr && (
            <div data-testid="puzzle-error" className="text-red-400 text-sm">{puzzleErr}</div>
          )}

          {puzzle && (
            <div data-testid="puzzle-display" className="space-y-4">
              <div className="text-sm opacity-70">
                difficulty: <span className="text-emerald-400 font-medium">{puzzle.difficulty}</span> ·
                score {puzzle.difficulty_score} · id <code className="text-xs">{puzzle.id}</code>
              </div>
              <div className="flex flex-wrap gap-4">
                {puzzle.cards.map((c, idx) => (
                  <PlayerCard
                    key={idx}
                    player={c.displayed_player}
                    team={c.context_team}
                    startYear={c.context_start_year}
                    endYear={c.context_end_year}
                  />
                ))}
              </div>
              {revealed && <AnswerReveal correct={puzzle.correct_player} />}
              {validation && (
                <div
                  data-testid="validation-result"
                  className={`rounded-lg p-3 text-sm ${validation.valid ? "bg-emerald-500/10 border border-emerald-500/40 text-emerald-300" : "bg-red-500/10 border border-red-500/40 text-red-300"}`}
                >
                  valid: <strong>{String(validation.valid)}</strong> · intersection: {(validation.intersection || []).join(", ") || "(empty)"}
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
